/* Alva — GA4 view_cart (Sep 17 2026)

   Closes the GA4 contract gap recorded in claude/tracking-stack-audit-aug26.md:
   "view_cart only 16 (cart drawer never fires it — add push on drawer open)".
   Every change shipped to the cart drawer was unreadable in GA4 because the
   drawer produced no cart-view event at all.

   FIRES ON
     - cart drawer open  (dialog:before-show, plus the `open` attribute as a
       fallback — same two signals assets/alva-cart-runtime.js already watches)
     - /cart page load
   Once per open; never for an empty cart. Opening the drawer straight after an
   add-to-cart is a genuine cart view, so it fires there too — expect
   add_to_cart and view_cart back to back in that flow.

   ITEM IDS deliberately match Shopify's own Google-channel format,
   shopify_<COUNTRY>_<productId>_<variantId> (verified live in the Aug 26 audit:
   shopify_US_8227097542973_45002068951357). Do NOT "simplify" these to bare
   variant ids — view_cart would stop joining add_to_cart and purchase in GA4.

   DELIVERY — two pushes, on purpose:
     1. dataLayer.push({event:'view_cart', ecommerce:{...}})  — GTM format.
        NOTHING consumes this today; it is here so a GTM tag can be built later
        without touching the theme.
     2. gtag('event','view_cart', {..., send_to: GA4_ID})  — this is what
        actually reaches GA4, because GA4 on this store is loaded by the Shopify
        Google & YouTube channel via gtag, not by the GTM container.
   ⚠️ If a GA4 "view_cart" tag is ever added in GTM on this custom event, DELETE
   the gtag call below or the event doubles.

   No Liquid config: country comes from Shopify.country, currency and values
   from /cart.js, so this file works unchanged in every market and survives
   section re-renders (the listeners live on the drawer element and document).
*/
(function () {
  if (window.__alvaGa4ViewCart) return;
  window.__alvaGa4ViewCart = true;

  /* GA4 property for alva-cookware.us. Blank = send to every configured gtag
     destination (GA4 + AW-11223267428), which works but also logs the event in
     Google Ads where nothing uses it. */
  var GA4_ID = 'G-64VRXS14LF';

  var cfg = window.AlvaViewCartConfig || {};
  if (typeof cfg.ga4Id === 'string') GA4_ID = cfg.ga4Id;
  var DEBUG = !!cfg.debug;

  var drawerOpen = false;
  var inFlight = false;
  var boundNode = null;
  var openObserver = null;
  var pendingView = false;

  function drawer() {
    return document.getElementById('cart-drawer');
  }

  function country() {
    try {
      if (window.Shopify && window.Shopify.country) return window.Shopify.country;
    } catch (e) {}
    return 'US';
  }

  function isCartPage() {
    var path = window.location.pathname.replace(/\/+$/, '');
    return /(^|\/)cart$/.test(path);
  }

  function netLineTotals(cart) {
    var lines = cart.items || [];
    var totals = lines.map(function (line) {
      return typeof line.final_line_price === 'number' ? line.final_line_price : line.final_price * line.quantity;
    });
    var total = Number(cart.total_price);
    if (totals.every(Number.isInteger) && totals.reduce(function (sum, value) { return sum + value; }, 0) === total) return totals;

    var root = drawer();
    if (!root) return null;
    var rows = Array.from(root.querySelectorAll('line-item[data-alva-line-key]'));
    totals = lines.map(function (line) {
      var row = rows.find(function (candidate) {
        return candidate.dataset.alvaLineKey === line.key && Number(candidate.dataset.alvaQuantity) === line.quantity;
      });
      var marker = row && (row.matches('[data-alva-net-line-cents]') ? row : row.querySelector('[data-alva-net-line-cents]'));
      return marker && marker.dataset.alvaNetLineCents !== '' ? Number(marker.dataset.alvaNetLineCents) : null;
    });
    if (!totals.every(function (value) { return Number.isInteger(value) && value >= 0; })) return null;
    return totals.reduce(function (sum, value) { return sum + value; }, 0) === total ? totals : null;
  }

  function itemsFrom(cart, totals) {
    return (cart.items || []).map(function (line, index) {
      var item = {
        item_id: 'shopify_' + country() + '_' + line.product_id + '_' + line.variant_id,
        item_name: line.product_title || line.title,
        price: totals[index] / line.quantity / 100,
        quantity: line.quantity,
        index: index
      };
      var discount = (line.original_price || 0) * line.quantity - totals[index];
      if (discount > 0) item.discount = discount / line.quantity / 100;
      if (line.vendor) item.item_brand = line.vendor;
      if (line.product_type) item.item_category = line.product_type;
      if (line.variant_title) item.item_variant = line.variant_title;
      if (line.sku) item.sku = line.sku;
      return item;
    });
  }

  function send(cart) {
    if (!cart || !cart.item_count) { pendingView = false; return; }
    var totals = netLineTotals(cart);
    if (!totals) { pendingView = true; return; }
    pendingView = false;

    var payload = {
      currency: cart.currency || 'USD',
      value: (cart.total_price || 0) / 100,
      items: itemsFrom(cart, totals)
    };

    window.dataLayer = window.dataLayer || [];
    try {
      /* Clear any ecommerce object left by an earlier event so GTM cannot merge
         a stale items[] into this one. */
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event: 'view_cart', ecommerce: payload });
    } catch (e) {}

    if (typeof window.gtag === 'function') {
      var params = {
        currency: payload.currency,
        value: payload.value,
        items: payload.items
      };
      if (GA4_ID) params.send_to = GA4_ID;
      try { window.gtag('event', 'view_cart', params); } catch (e) {}
    }

    if (DEBUG) console.log('[Alva] view_cart', payload);
  }

  function fetchAndSend() {
    if (inFlight) return;
    inFlight = true;
    /* /cart.js rather than the rendered DOM: it is correct immediately after an
       add, a quantity change or an Onward protection line, and it does not care
       what the drawer markup currently looks like. */
    fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) {
        inFlight = false;
        if (!cart) return;
        send(cart);
        if (pendingView && drawerOpen) window.setTimeout(function () {
          if (pendingView && drawerOpen) send(cart);
        }, 150);
      })
      .catch(function () { inFlight = false; });
  }

  function opened() {
    if (drawerOpen) return;
    drawerOpen = true;
    fetchAndSend();
  }

  function sync() {
    var node = drawer();
    bind(node);
    if (!node) { drawerOpen = false; return; }
    if (node.hasAttribute('open')) opened();
    else drawerOpen = false;
  }

  function bind(node) {
    if (boundNode === node) return;
    if (openObserver) openObserver.disconnect();
    boundNode = node;
    if (!node) return;
    node.addEventListener('dialog:before-show', opened);
    node.addEventListener('dialog:before-hide', function () { drawerOpen = false; pendingView = false; });
    openObserver = new MutationObserver(sync);
    openObserver.observe(node, { attributes: true, attributeFilter: ['open'] });
  }

  function init() {
    sync();
    document.addEventListener('shopify:section:load', sync);
    document.addEventListener('cart-drawer:refreshed', function () {
      if (drawerOpen && pendingView) fetchAndSend();
    });
    window.addEventListener('pageshow', sync);
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-drawer').then(sync);
    }
    /* The /cart page is a cart view with no drawer to open. */
    if (isCartPage()) fetchAndSend();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

